import { useIntl } from 'react-intl';
import { Dispatch, SetStateAction } from 'react';
import { DialogWindow } from './Dialog';

interface ChangelogDialogProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}

export const ChangelogDialog = ({
  open,
  onOpenChange,
}: ChangelogDialogProps) => {
  const { formatMessage } = useIntl();
  return (
    <DialogWindow
      title={formatMessage({
        defaultMessage: 'Ändringslogg',
      })}
      content={<ChangelogContent />}
      open={open}
      onOpenChange={onOpenChange}
      useFooter={true}
    />
  );
};

const ChangelogContent = () => {
  return (
    <div className="space-y-6 text-text-950">
      <ChangelogVersion
        version="2026-12"
        date="2026-11-05"
        changes={['Uppdaterad rättighetshantering för administratörer.']}
      />
      <ChangelogVersion
        version="2026-11: Skolmodulen"
        date="2026-10-26"
        changes={[
          'Helt ny huvudfunktion i portalen: Skolmodulen. Inkluderar import och administration av barn/elever/ungdomar, automatiskt utställande av biljetter och resekort, samt konfiguration av detta. Mer info om detta finns tillgänligt i portalen för de som har tillgång till funktionen.',
          'Ny funktion: masshantering av tjänsteresa i app.',
          'Vid automatisk utloggning tas man nu tillbaka till sidan man var på.',
        ]}
      />
      <ChangelogVersion
        version="2026-10"
        date="2026-10-20"
        changes={[
          'Ny roll: bärarhantering. Enbart läsrättighet men kan administrera bärare för resenärer.',
          'Det går nu att sätta C/O på adresser på barn och ungdomar.',
          'Vilken organisation man är med i visas nu here i högre hörnet på dator.',
        ]}
      />
      <ChangelogVersion
        version="2026-9"
        date="2026-10-12"
        changes={[
          'Ny funktion: explicit korthistorik för systemägda resekort.',
          'Tog bort möjlighet att dölja systemägda kort.',
          'E-post i appen heter nu "E-post i appen / Mina sidor".',
          'Biljetter visar Bobcat order-ID för köpet som utfärdade biljetten, även vid delbetalning och kostnadsfria perioder. En transaktion utan eget Bobcat-köp visar biljettens order-ID.',
        ]}
      />
      <ChangelogVersion
        version="2026-8"
        date="2026-10-08"
        changes={[
          'Ny funktion: knapp för systemadministratörer att manuellt rensa resekort.',
          'Ny funktion: avslutade biljetter sparar och visar anledningen till avslutningen. Vid manuellt avslut har man möjlighet att ange text själv, och biljettdetaljerna visar vem som avslutade biljetten.',
        ]}
      />
      <ChangelogVersion
        version="2026-7"
        date="2026-09-30"
        changes={[
          'Köpställe "Skicka biljett" heter nu "Portalen".',
          'Löste en bugg i hämtning av transaktionsdetaljer för en biljett.',
          'Löste en bugg gällande hur biljettstatus visas.',
        ]}
      />
      <ChangelogVersion
        version="2026-6"
        date="2026-08-29"
        changes={[
          'Stor ändring i hur kort och kopplingar mot biljettsystemet hanteras. Varje kort får bara finnas på en resenär åt gången och det går bara att lägga in helt "blanka" kort. Privata kort måste komma från "Epost i appen".',
          'Löste en bugg som gjorde att resekort inte hanterades rätt för borttagna resenärer.',
        ]}
      />
      <ChangelogVersion
        version="2026-5"
        date="2026-06-30"
        changes={[
          'Ny funktion: global sökning av resenärer och kortnummer för systemadministratörer.',
        ]}
      />
      <ChangelogVersion
        version="2026-4"
        date="2026-06-23"
        changes={[
          'Ny funktion: gruppresor. I biljettköpsflödet går det nu att ange flera resenärsklasser samtidigt.',
        ]}
      />
      <ChangelogVersion
        version="2026-3"
        date="2026-05-29"
        changes={[
          'Ny funktion: självbetjäning för resenärer, elever och vårdnadshavare. Gör det möjligt att se över sina biljetter och bärare. Inloggning via magic-link.',
        ]}
      />
      <ChangelogVersion
        version="2026-2"
        date="2026-05-08"
        changes={[
          'Ny funktion: "saldokoll" (visa antal klipp kvar på klipp-biljetter).',
          'Uppdaterat inloggningsflödet för att slippa ange verifieringskod lika ofta.',
          'Löste en bugg som gjorde att det inte gick att använda själva scroll-rutan i dialoger.',
          'Löste en bugg där medlemskap/resenärer inte alltid blev inaktiverade vid utsatt datum.',
          'Går nu att söka efter fakturareferens i organisationslistan.',
        ]}
      />
      <ChangelogVersion
        version="2026-1"
        date="2026-01-23"
        changes={[
          'Spara vilka avdelningar som ska ha tillgång till varje fil. Kommer bara gälla nya filer.',
          'Mer detaljer (info om fakturamottagare) i exportfilerna för transaktioner. Var tidigare lite begränsat för sysadmins.',
          'När ny resenär läggs till manuellt är kontrollen av duplicerad information inte lika "aggressiv".',
          'Åtgärdat en bugg som gjorde att profilbyte inte alltid gick igenom helt.',
          'Det var möjligt att klicka in på att lägga till tjänsteresa på kort på resenärer som redan har tjänsteresa på kort på alla sina avdelningar.',
          'Stoppat transaktionsdetaljer från att kunna hamna utanför sin box i listvyn.',
        ]}
      />
      <ChangelogVersion
        version="Företagsmodulen klar"
        date="2025-12-01"
        changes={['Första versionen av företagsmodulen av portalen.']}
      />
    </div>
  );
};

interface ChangelogVersionProps {
  version: string;
  date?: string;
  changes: string[];
}

const ChangelogVersion = ({
  version,
  date,
  changes,
}: ChangelogVersionProps) => {
  return (
    <div className="border-b border-background-300 pb-4 last:border-b-0">
      <h3 className="mb-2 text-lg font-semibold text-text-950">
        {version}
        <span className="ml-2 text-sm font-normal text-text-700">
          ({date ? `${date}` : 'Kommande uppdatering'})
        </span>
      </h3>
      <ul className="list-disc space-y-1 pl-5">
        {changes.map((change, index) => (
          <li key={index} className="text-sm">
            {change}
          </li>
        ))}
      </ul>
    </div>
  );
};
